import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';

import imgixConfig from 'Config/imgix';
import env from 'Env';
import Meta from 'Components/Utility/SEO/Meta';
import NoIndex from 'Components/Utility/SEO/NoIndex';
import QuickLinks from 'Components/QuickLinks/QuickLinks';
import isPrerender from 'Helpers/SEO/is-prerender';
import store from 'Redux/store';
import GamesSection from 'Components/AlgoliaSections/GamesSection';
import fetchAutoSearch from 'Redux/ducks/algolia-autosearch';
import ProductCarousel from 'Components/HomePage/ProductCarousel';
import { MetaTag } from 'Helpers/SEO/Tags';

const messages = defineMessages({
  title: {
    id: 'notfound.title',
    defaultMessage: 'Page Not Found',
  },
});

function NotFound({ intl, redirect, pathname, siteTheme }) {
  const [searchHits, setHits] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let isProductPath = false;
    let valueToSearch = '';
    if (includes(pathname, '/game/') || includes(pathname, '/bundle/')) {
      isProductPath = true;
      valueToSearch = pathname.split('/')[2];
    }

    const fetchHits = async () => {
      const newHits = await fetchAutoSearch(valueToSearch, 8, '');
      setHits(newHits.hits);
    };

    if (isProductPath) {
      fetchHits().catch(setFetchError);
    }
  }, [pathname]);

  if (fetchError) {
    throw fetchError;
  }

  const { formatMessage } = intl;
  store.dispatch({ type: 'frontend/not-found/NOT_FOUND', payload: { notFound: true } });

  if (redirect && !isPrerender()) {
    return <Redirect to={`/${redirect}`} />;
  }

  return (
    <main>
      <Meta title={formatMessage(messages.title)} />
      {!redirect &&
        <NoIndex />
      }
      {redirect &&
        <>
          <MetaTag name="prerender-status-code" content="301" />
          <MetaTag name="prerender-header" content={`Location: https://${env.appUrl}/${window.locale || 'en'}/${redirect}`} />
        </>
      }
      <div className="container PageNotFound">
        <header className="PageNotFound__header">
          <div className="PageNotFound__header__details">
            <h1 className="PageNotFound__header__details__heading">
              <FormattedMessage id="PageNotFound.header.h1" defaultMessage="Ooops! Page not found!" />
            </h1>
            <p className="PageNotFound__header__details__text">
              <FormattedMessage id="PageNotFound.header.text" defaultMessage="We're unable to find the page you're looking for. Try using the search bar above or head over to the homepage." />
            </p>
            <Link to="/" className="PageNotFound__header__details__button btn btn-primary">
              <FormattedMessage id="PageNotFound.header.button" defaultMessage="Go to the homepage" />
            </Link>
          </div>
          <div className="PageNotFound__header__img">
            {siteTheme === 'light' &&
              <img src={`https://fanatical.imgix.net/production/brand/404/404-graphic-light.png?${imgixConfig}`} alt="404" />
            }
            {siteTheme === 'dark' &&
              <img src={`https://fanatical.imgix.net/production/brand/404/404-graphic-dark.png?${imgixConfig}`} alt="404" />
            }
          </div>
        </header>

        {!isEmpty(searchHits) &&
          <section className="">
            <div className="section-heading-container">
              <h2>
                <FormattedMessage id="PageNotFound.search-hits.title" defaultMessage="Here's what we think you were looking for" />
              </h2>
            </div>
            <ProductCarousel
              leftAlign
              showFakeCards
              hitData={searchHits}
              slidesToShow={4}
            />
          </section>
        }
        <section className="">
          <div className="section-heading-container">
            <h2>
              <FormattedMessage id="PageNotFound.top-sellers.title" defaultMessage="Explore our top-selling products" />
            </h2>
          </div>
          <GamesSection hideTitle />
        </section>
        <div className="row">
          <QuickLinks />
        </div>
      </div>
    </main>
  );
}

NotFound.propTypes = {
  redirect: PropTypes.string,
  siteTheme: PropTypes.string,
};

NotFound.defaultProps = {
  redirect: '',
  siteTheme: 'dark',
};

const mapStateToProps = ({
  router: { location: { pathname } },
  siteTheme,
}) => ({
  pathname,
  siteTheme,
});

export default connect(mapStateToProps)(injectIntl(NotFound));
